import React, { useEffect, useMemo, useState } from 'react'

import { ApolloProvider } from '@apollo/client'
import { FlagProvider } from '@unleash/proxy-client-react'
import * as queryString from 'query-string'
import { BrowserRouter, Routes, Route } from 'react-router-dom'

import Splash from '~/components/Loading/Splash'
import config, { isGuestRoute } from '~/config'

import { isEmbedded, isProdAdminEmbedded } from '~/helper/isEmbedded'

import { createApolloClient } from '~/modules/api'

import Authentication, { clearAuthToken, logout, redirectToLogin, setAuthToken } from '~/modules/auth'

import AdminRoot from '~/pages/AdminRoot'
import SessionExpired from '~/pages/Errors/SessionExpired'
import Unexpected from '~/pages/Errors/Unexpected'
import GuestRoot from '~/pages/GuestRoot'
import PrivateRoot from '~/pages/Root'

import ErrorBoundary from './ErrorBoundary'

const App = () => {
  const [expired, setExpired] = useState(false)
  const [refreshing, setRefreshing] = useState(true)

  const unleashConfig = config.UNLEASH_CLIENT_KEY
    ? {
        url: config.UNLEASH_URL,
        clientKey: config.UNLEASH_CLIENT_KEY,
        refreshInterval: 15,
        appName: config.UNLEASH_APP_NAME,
        context: {
          properties: {
            platform: 'web',
          },
        },
      }
    : undefined

  const apolloClient = useMemo(() => {
    if (!config.GOLDEN_GATEWAY_API || !config.HODOR_API) return null
    const onAuthExpire = () => setExpired(true)
    return createApolloClient(config.GOLDEN_GATEWAY_API, config.HODOR_API, onAuthExpire)
  }, [])

  useEffect(() => {
    if (isGuestRoute) {
      setRefreshing(false)
      return
    }

    const queryParams = queryString.parse(location.search)
    const queryAuthToken = queryParams.authToken

    if (isEmbedded && isProdAdminEmbedded && !queryAuthToken) {
      window.parent.postMessage({ type: 'getAccessToken', message: undefined }, '*')

      const messageHandler = (event: MessageEvent) => {
        if (event.data?.message) {
          setAuthToken(event.data.message)
          setRefreshing(false)
          window.removeEventListener('message', messageHandler)
        }
      }

      if (window.addEventListener) {
        window.addEventListener('message', messageHandler, false)
      }
    } else {
      if (queryAuthToken) {
        setAuthToken(queryAuthToken as string)
        setRefreshing(false)
      } else {
        clearAuthToken()

        Authentication(config.HODOR_API!)
          .refresh()
          .then((token) => {
            setAuthToken(token)
            setRefreshing(false)
          })
          .catch(() => logout().then(redirectToLogin))
      }
    }
  }, [])
  if (expired) return <SessionExpired />
  if (refreshing) return <Splash />
  if (!apolloClient) return <Unexpected />

  return (
    <ErrorBoundary>
      <FlagProvider config={unleashConfig}>
        <ApolloProvider client={apolloClient}>
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <Routes>
              <Route path="/admin/*" element={<AdminRoot />} />
              <Route path="/guest/*" element={<GuestRoot />} />
              <Route path="/*" element={<PrivateRoot />} />
            </Routes>
          </BrowserRouter>
        </ApolloProvider>
      </FlagProvider>
    </ErrorBoundary>
  )
}

App.displayName = 'App'

export default App
